// ============================================================
// OperationalTab Component for Schedules.js
// ============================================================
//
// DATA MODEL:
//   Screens come from:     screens collection (filtered by userId)
//   Schedules saved to:    screens/{screenId}/operationalSchedule/config
//   Device evaluates locally every 10 seconds
//
// USAGE IN SCHEDULES.JS:
//   1. Import at the top of Schedules.js:
//      import OperationalTab from './OperationalTab';
//
//   2. Place <OperationalTab /> in JSX where activeTab === 'operational':
//      {activeTab === 'operational' && <OperationalTab />}
//
//   3. Add the tab button in the header alongside Content and TV Power.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, getDoc, setDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { Monitor, Power, Save, Trash2, X } from 'lucide-react';

const DAYS_OF_WEEK = [
  { key: 'mon', label: 'M', full: 'Mon' },
  { key: 'tue', label: 'T', full: 'Tue' },
  { key: 'wed', label: 'W', full: 'Wed' },
  { key: 'thu', label: 'T', full: 'Thu' },
  { key: 'fri', label: 'F', full: 'Fri' },
  { key: 'sat', label: 'S', full: 'Sat' },
  { key: 'sun', label: 'S', full: 'Sun' },
];

const DEFAULT_SCHEDULE = {
  enabled: false,
  timezone: 'America/New_York',
  blocks: [
    {
      id: 1,
      startTime: '06:00',
      endTime: '21:00',
      days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
    },
  ],
};

function OperationalTab() {
  const { currentUser, isAdmin } = useAuth();
  const userId = currentUser?.uid;

  // Screen list
  const [screens, setScreens] = useState([]);
  const [selectedScreenId, setSelectedScreenId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMobileSheet, setShowMobileSheet] = useState(false);

  // Track which screens have an enabled schedule (screenId → boolean)
  const [activeSchedules, setActiveSchedules] = useState({});

  // Schedule for selected screen
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // ─── Fetch screens owned by this user ───────────────────────
  useEffect(() => {
    if (!userId) return;

    const fetchScreens = async () => {
      setLoading(true);
      try {
        const screensQuery = isAdmin
          ? collection(db, 'screens')
          : query(collection(db, 'screens'), where('userId', '==', userId));

        const snapshot = await getDocs(screensQuery);
        const screensList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort by screen name
        screensList.sort((a, b) =>
          (a.screenName || '').localeCompare(b.screenName || '')
        );
        console.log('OperationalTab userId:', userId);
        console.log('OperationalTab screens found:', screensList.length, screensList);

        // Filter: operational-capable screens, owner/admin-scoped (mirrors the
        // Content tab in Schedules.js). Admin sees own + shared/unowned screens
        // (userId null) but NOT other users' owned screens; a user sees only theirs.
        const filteredScreens = screensList.filter((screen) => {
            if (screen.supportsOperationalControl !== true) return false;
            if (isAdmin) return !screen.userId || screen.userId === currentUser?.uid;
            return screen.userId === currentUser?.uid;
        });

        setScreens(filteredScreens);

        // Auto-select first screen
        if (screensList.length > 0 && !selectedScreenId) {
          setSelectedScreenId(screensList[0].id);
        }

        // Fetch active schedules for sidebar dots
        const scheduleStatus = {};
        for (const screen of screensList) {
          try {
            const schedRef = doc(db, 'screens', screen.id, 'operationalSchedule', 'config');
            const schedSnap = await getDoc(schedRef);
            if (schedSnap.exists()) {
              const data = schedSnap.data();
              if (data.enabled && data.blocks && data.blocks.length > 0) {
                scheduleStatus[screen.id] = true;
              }
            }
          } catch (err) {
            // Ignore individual screen errors
          }
        }
        setActiveSchedules(scheduleStatus);
      } catch (error) {
        console.error('Error fetching screens:', error);
        setScreens([]);
      } finally {
        setLoading(false);
      }
    };

    fetchScreens();
  }, [userId, selectedScreenId, isAdmin]);

  // ─── Load schedule when a screen is selected ────────────────
  const loadSchedule = useCallback(async (screenId) => {
    if (!screenId) return;

    setScheduleLoading(true);
    setHasChanges(false);
    setSaveSuccess(false);

    try {
      const schedRef = doc(db, 'screens', screenId, 'operationalSchedule', 'config');
      const schedSnap = await getDoc(schedRef);

      if (schedSnap.exists()) {
        const data = schedSnap.data();
        // Ensure blocks have IDs
        const blocks = (data.blocks || []).map((b, i) => ({
          ...b,
          id: b.id || i + 1,
        }));
        setSchedule({
          enabled: data.enabled || false,
          timezone: data.timezone || 'America/New_York',
          blocks: blocks.length > 0 ? blocks : DEFAULT_SCHEDULE.blocks,
        });
      } else {
        setSchedule(JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)));
      }
    } catch (error) {
      console.error('Error loading schedule:', error);
      setSchedule(JSON.parse(JSON.stringify(DEFAULT_SCHEDULE)));
    } finally {
      setScheduleLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedScreenId) {
      loadSchedule(selectedScreenId);
    }
  }, [selectedScreenId, loadSchedule]);

  // ─── Save schedule ─────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedScreenId || !schedule) return;

    setSaving(true);
    try {
      const schedRef = doc(db, 'screens', selectedScreenId, 'operationalSchedule', 'config');

      // Clean blocks for Firestore (remove client-side id field)
      const cleanBlocks = schedule.blocks.map(({ id, ...rest }) => rest);

      await setDoc(schedRef, {
        enabled: schedule.enabled,
        timezone: schedule.timezone,
        blocks: cleanBlocks,
        updatedAt: Date.now(),
      });

      setHasChanges(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);

      // Update sidebar dot
      setActiveSchedules((prev) => {
        const updated = { ...prev };
        if (schedule.enabled && schedule.blocks.length > 0) {
          updated[selectedScreenId] = true;
        } else {
          delete updated[selectedScreenId];
        }
        return updated;
      });
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Schedule mutation helpers ─────────────────────────────
  const updateSchedule = (updates) => {
    setSchedule((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const updateBlock = (blockId, field, value) => {
    setSchedule((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) =>
        b.id === blockId ? { ...b, [field]: value } : b
      ),
    }));
    setHasChanges(true);
  };

  const toggleBlockDay = (blockId, day) => {
    setSchedule((prev) => ({
      ...prev,
      blocks: prev.blocks.map((b) => {
        if (b.id !== blockId) return b;
        const days = b.days.includes(day)
          ? b.days.filter((d) => d !== day)
          : [...b.days, day];
        return { ...b, days };
      }),
    }));
    setHasChanges(true);
  };

  const addBlock = () => {
    const maxId = schedule.blocks.reduce((max, b) => Math.max(max, b.id || 0), 0);
    setSchedule((prev) => ({
      ...prev,
      blocks: [
        ...prev.blocks,
        {
          id: maxId + 1,
          startTime: '08:00',
          endTime: '17:00',
          days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        },
      ],
    }));
    setHasChanges(true);
  };

  const removeBlock = (blockId) => {
    setSchedule((prev) => ({
      ...prev,
      blocks: prev.blocks.filter((b) => b.id !== blockId),
    }));
    setHasChanges(true);
  };

  // ─── Helper: format time for display ───────────────────────
  const formatTime12 = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  // ─── Helper: generate weekly preview bar ───────────────────
  const renderWeekPreview = () => {
    if (!schedule || !schedule.blocks.length) return null;

    const dayGroups = {};
    DAYS_OF_WEEK.forEach((d) => {
      dayGroups[d.key] = [];
    });

    schedule.blocks.forEach((block) => {
      (block.days || []).forEach((day) => {
        if (dayGroups[day]) {
          dayGroups[day].push(block);
        }
      });
    });

    return (
      <div style={{ marginTop: '24px' }}>
        <h4
          style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#6b7280',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '12px',
          }}
        >
          Weekly Preview
        </h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {DAYS_OF_WEEK.map((day) => (
            <div
              key={day.key}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <span
                style={{
                  width: '36px',
                  fontSize: '11px',
                  color: '#9ca3af',
                  fontWeight: '500',
                }}
              >
                {day.full}
              </span>
              <div
                style={{
                  flex: 1,
                  height: '16px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '4px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {dayGroups[day.key].map((block, i) => {
                  const [sh, sm] = (block.startTime || '00:00').split(':').map(Number);
                  const [eh, em] = (block.endTime || '23:59').split(':').map(Number);
                  const startMin = sh * 60 + sm;
                  const endMin = eh * 60 + em;
                  const left = (startMin / 1440) * 100;
                  const width = ((endMin - startMin) / 1440) * 100;

                  return (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${Math.max(width, 1)}%`,
                        top: '2px',
                        bottom: '2px',
                        backgroundColor: schedule.enabled ? '#10b981' : '#9ca3af',
                        borderRadius: '2px',
                        opacity: 0.8,
                      }}
                      title={`${formatTime12(block.startTime)} – ${formatTime12(block.endTime)}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
          {/* Time axis labels */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ width: '36px' }} />
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '9px',
                color: '#d1d5db',
              }}
            >
              <span>12A</span>
              <span>6A</span>
              <span>12P</span>
              <span>6P</span>
              <span>12A</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ─── Mobile screen selection handler ──────────────────────────
  const handleMobileScreenSelect = (screen) => {
    setSelectedScreenId(screen.id);
    setShowMobileSheet(true);
  };

  // ─── Reusable schedule editor (used in desktop panel + mobile bottom sheet) ──
  const renderScheduleEditor = () => {
    const selectedScreen = screens.find((s) => s.id === selectedScreenId);

    if (scheduleLoading) {
      return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px', color: '#9ca3af' }}>Loading schedule...</div>;
    }
    if (!schedule) {
      return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 20px', color: '#9ca3af' }}>Select a screen to edit its operational schedule.</div>;
    }

    return (
      <>
        {/* Header with screen name and enable toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Power size={20} style={{ color: schedule.enabled ? '#10b981' : '#9ca3af' }} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
              {selectedScreen?.screenName || 'Screen'}
            </h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '13px', color: schedule.enabled ? '#10b981' : '#9ca3af', fontWeight: '500' }}>
              Schedule {schedule.enabled ? 'On' : 'Off'}
            </span>
            <div onClick={() => updateSchedule({ enabled: !schedule.enabled })} role="button" tabIndex={0} style={{ width: '44px', height: '24px', borderRadius: '12px', backgroundColor: schedule.enabled ? '#10b981' : '#d1d5db', cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s' }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: '2px', left: schedule.enabled ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
          </div>
        </div>

        {/* Timezone selector */}
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Timezone</h4>
          <select value={schedule.timezone} onChange={(e) => updateSchedule({ timezone: e.target.value })} style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', background: 'white', cursor: 'pointer' }}>
            <option value="America/New_York">Eastern (ET)</option>
            <option value="America/Chicago">Central (CT)</option>
            <option value="America/Denver">Mountain (MT)</option>
            <option value="America/Los_Angeles">Pacific (PT)</option>
          </select>
        </div>

        {/* Time blocks */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 0 }}>Time Blocks</h4>
            <span onClick={addBlock} role="button" tabIndex={0} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', border: 'none', backgroundColor: 'transparent', fontSize: '13px', fontWeight: '600', color: '#E63946', cursor: 'pointer' }}>+ Add Block</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {schedule.blocks.map((block) => (
              <div key={block.id} style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>ON</span>
                  <input type="time" value={block.startTime} onChange={(e) => updateBlock(block.id, 'startTime', e.target.value)} onClick={(e) => e.stopPropagation()} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #86efac', backgroundColor: '#f0fdf4', fontSize: '14px', color: '#166534', outline: 'none', cursor: 'text', width: '130px', WebkitAppearance: 'none', MozAppearance: 'none' }} />
                  <span style={{ color: '#9ca3af', fontSize: '13px' }}>→</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280' }}>OFF</span>
                  <input type="time" value={block.endTime} onChange={(e) => updateBlock(block.id, 'endTime', e.target.value)} onClick={(e) => e.stopPropagation()} style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #fca5a5', backgroundColor: '#fef2f2', fontSize: '14px', color: '#991b1b', outline: 'none', cursor: 'text', width: '130px', WebkitAppearance: 'none', MozAppearance: 'none' }} />
                  {schedule.blocks.length > 1 && (
                    <span onClick={() => removeBlock(block.id)} role="button" tabIndex={0} style={{ marginLeft: 'auto', padding: '6px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#9ca3af', borderRadius: '4px', display: 'inline-flex' }} title="Remove block">
                      <Trash2 size={16} />
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {DAYS_OF_WEEK.map((day) => {
                    const active = (block.days || []).includes(day.key);
                    return (
                      <div key={day.key} onClick={() => toggleBlockDay(block.id, day.key)} role="button" tabIndex={0} style={{ width: '34px', height: '34px', borderRadius: '50%', border: active ? 'none' : '1px solid #d1d5db', backgroundColor: active ? '#E63946' : '#fff', color: active ? '#fff' : '#9ca3af', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none' }}>
                        {day.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Weekly preview */}
        {renderWeekPreview()}

        {/* Save button */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div onClick={hasChanges && !saving ? handleSave : undefined} role="button" tabIndex={0} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', borderRadius: '8px', border: 'none', backgroundColor: !hasChanges ? '#d1d5db' : saving ? '#fca5a5' : '#E63946', color: '#fff', fontSize: '13px', fontWeight: '500', cursor: hasChanges && !saving ? 'pointer' : 'default', transition: 'background-color 0.2s', userSelect: 'none' }}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Schedule'}
          </div>
          {saveSuccess && <span style={{ fontSize: '13px', color: '#10b981', fontWeight: '500' }}>✓ Saved</span>}
          {hasChanges && !saveSuccess && <span style={{ fontSize: '12px', color: '#f59e0b' }}>Unsaved changes</span>}
        </div>
      </>
    );
  };

  // ─── Loading state ─────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 20px',
          color: '#9ca3af',
        }}
      >
        Loading screens...
      </div>
    );
  }

  // ─── No screens found ──────────────────────────────────────
  if (screens.length === 0) {
    return (
      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            width: '280px',
            flexShrink: 0,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            padding: '20px',
          }}
        >
          <h3
            style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#E63946',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '16px',
            }}
          >
            Operational Screens
          </h3>
          <p
            style={{
              fontSize: '14px',
              color: '#9ca3af',
              textAlign: 'center',
              padding: '20px 0',
            }}
          >
            No screens found.
          </p>
        </div>
        <div
          style={{
            flex: 1,
            backgroundColor: '#fff',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            padding: '40px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <p style={{ color: '#9ca3af', fontSize: '15px' }}>
            No screens available for operational scheduling.
          </p>
        </div>
      </div>
    );
  }


  // ─── Main render ───────────────────────────────────────────
  const selectedScreen = screens.find((s) => s.id === selectedScreenId);

  return (
    <>
      {/* Desktop: side-by-side layout */}
      <div className="sidebar-layout desktop-only" style={{ display: 'flex', gap: '20px' }}>
        <div style={{ width: '280px', flexShrink: 0, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
          <h3 style={{ fontSize: '12px', fontWeight: '600', color: '#E63946', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '16px' }}>Operational Screens</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {screens.map((screen) => {
              const isSelected = selectedScreenId === screen.id;
              return (
                <div key={screen.id} onClick={() => setSelectedScreenId(screen.id)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedScreenId(screen.id); }} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', backgroundColor: isSelected ? 'rgba(230,57,70,0.08)' : 'transparent', transition: 'background-color 0.15s', userSelect: 'none' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f9fafb'; }} onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                  <Monitor size={16} style={{ color: isSelected ? '#E63946' : '#6b7280', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: isSelected ? '600' : '500', color: isSelected ? '#E63946' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{screen.screenName || screen.name || screen.id}</div>
                  </div>
                  {activeSchedules[screen.id] && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0 }} title="Schedule active" />}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '24px' }}>
          {renderScheduleEditor()}
        </div>
      </div>

      {/* Mobile: Screen list */}
      <div className="mobile-only">
        <div className="mobile-list">
          {screens.map((screen) => (
            <div key={screen.id} className="mobile-list-item" onClick={() => handleMobileScreenSelect(screen)}>
              <Monitor size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
              <div className="mobile-list-content">
                <div className="mobile-list-title">{screen.screenName || screen.name || screen.id}</div>
                <div className="mobile-list-subtitle">Operational</div>
              </div>
              {activeSchedules[screen.id] && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', flexShrink: 0, marginRight: '8px' }} />}
            </div>
          ))}
          {screens.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>No screens available for operational scheduling.</div>}
        </div>
      </div>

      {/* Mobile: Bottom Sheet */}
      {showMobileSheet && selectedScreenId && (
        <div className="mobile-only">
          <div className="bottom-sheet-overlay" onClick={() => setShowMobileSheet(false)} />
          <div className="bottom-sheet">
            <div className="bottom-sheet-header">
              <h3>{selectedScreen?.screenName || selectedScreen?.name || selectedScreenId}</h3>
              <button className="bottom-sheet-close" onClick={() => setShowMobileSheet(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="bottom-sheet-content">
              {renderScheduleEditor()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OperationalTab;